import axios from 'axios'
import ConfigAPI, { waitConfig } from '@/config/api' // ← exportá waitConfig como te mostré
import { Session, actualizarSoloToken } from '@/handler/session'
import router from '@/router'
import { backgroundTaskQueue } from '@/utils/backgroundTaskQueue'
import { emitBackgroundReportCompleted } from '@/utils/backgroundReportBus'
import { emitPersistentReportsChanged } from '@/utils/persistentReportBus'
import { confirmPersistentReportSubmission } from '@/utils/persistentReportConfirmation'
import { v4 as uuidv4 } from 'uuid'

// crear instancias SIN baseURL (se setea luego)
const instance = axios.create({
  timeout: 1800000,
  headers: { 'Content-Type': 'application/json' },
})

const refreshAxios = axios.create({
  // 30* 60* 1000 ( x son milisegundos)
  timeout: 1800000,
  headers: { 'Content-Type': 'application/json' },
})

const normalizeUrlForLabel = (url = '') => {
  if (!url) return 'request'
  const clean = url.split('?')[0]
  const chunks = clean.split('/').filter(Boolean)
  return chunks.slice(-2).join('/') || clean
}

const shouldTrackRequest = (config = {}) => {
  if (config.queue?.track === false) return false
  const url = config.url || ''
  if (url.includes('refresh-token')) return false
  return true
}

// asegura baseURL antes de cada request
const ensureBaseURL = async (cfg) => {
  if (!instance.defaults.baseURL) {
    await waitConfig(); // espera /config.json o lo que uses
    if (ConfigAPI.baseURL) {
      instance.defaults.baseURL = ConfigAPI.baseURL
      refreshAxios.defaults.baseURL = ConfigAPI.baseURL
    }
  }
  // usa la base por default si el config puntual no la trae
  cfg.baseURL ||= instance.defaults.baseURL
  return cfg
}

// ✅ Interceptor REQUEST: baseURL + token
instance.interceptors.request.use(async (config) => {
  await ensureBaseURL(config)

  // La confirmación se centraliza aquí para cubrir todos los listados que
  // usan reportes persistentes, presentes y futuros.
  if (config.queue?.persistentReport && !config.metadata?.persistentReportConfirmed) {
    const confirmed = await confirmPersistentReportSubmission()
    if (!confirmed) {
      const error = new Error('Solicitud de reporte cancelada')
      error.code = 'PERSISTENT_REPORT_CANCELLED'
      return Promise.reject(error)
    }
    config.metadata = { ...(config.metadata || {}), persistentReportConfirmed: true }
  }

  // Los reportes persistentes siempre se envían a la cola. Centralizarlo acá
  // evita repetir UUID, headers y el flag S/N en cada pantalla de listado.
  if (config.queue?.persistentReport && !config.metadata?.persistentReportJobId) {
    const jobId = uuidv4()
    const reportForm = config.queue.formSnapshot || config.queue.completion?.formSnapshot || {}
    config.params = { ...(config.params || {}), utilizar_websocket_sino: 'S' }
    config.headers = {
      ...(config.headers || {}),
      'X-Job-Id': jobId,
      'X-Report-Output': config.queue.output || 'PDF',
      'X-Report-Parameters': encodeURIComponent(JSON.stringify(config.queue.parameters || [])),
      'X-Report-Form': encodeURIComponent(JSON.stringify(reportForm)),
    }
    config.metadata = { ...(config.metadata || {}), persistentReportJobId: jobId }
  }

  if (shouldTrackRequest(config) && !config.metadata?.queueJobId) {
    const method = String(config.method || 'get').toUpperCase()
    const label = config.queue?.label || `${method} ${normalizeUrlForLabel(config.url)}`
    const queueJobId = backgroundTaskQueue.trackHttpStart({ label })
    config.metadata = { ...(config.metadata || {}), queueJobId }
  }

  const token = Session.getToken()
  if (token) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` }
  }
  return config
})

// (opcional) también para refreshAxios por si lo usás en otros lados
refreshAxios.interceptors.request.use(async (config) => {
  await ensureBaseURL(config)
  return config
})

// ✅ Interceptor RESPONSE: refresh de token
instance.interceptors.response.use(
  (response) => {
    const queueJobId = response.config?.metadata?.queueJobId
    if (queueJobId) {
      backgroundTaskQueue.trackHttpSuccess(queueJobId)
    }

    const queueConfig = response.config?.queue
    const status = response.data?.status
    if (queueConfig?.persistentReport && ['enqueued', 'queued', 'processing', 'running'].includes(status)) {
      emitPersistentReportsChanged()
    } else if (queueConfig?.completion) {
      emitBackgroundReportCompleted({
        queueJobId,
        completion: queueConfig.completion,
        responseData: response.data,
      })
    }

    return response
  },
  async (error) => {
    const originalRequest = error.config || {}

    if (error?.code === 'PERSISTENT_REPORT_CANCELLED') {
      return Promise.reject(error)
    }

    const status401 = error.response?.status === 401
    const isRefresh = (originalRequest.url || '').includes('refresh-token')
    const hasRefresh = !!Session.getRefreshToken()

    if (status401 && !originalRequest._retry && !isRefresh && hasRefresh) {
      originalRequest._retry = true
      try {
        // pedir nuevo access token
        const res = await refreshAxios.post('refresh-token', null, {
          headers: { Authorization: `Bearer ${Session.getRefreshToken()}` },
        })
        const newAccessToken = res.data?.token
        if (!newAccessToken) throw new Error('Access token vacío')

        actualizarSoloToken(newAccessToken)

        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${newAccessToken}`,
        }

        // reintentar con la misma instancia (mantiene interceptores/baseURL)
        return instance(originalRequest)
      } catch (e) {
        const queueJobId = originalRequest?.metadata?.queueJobId
        if (queueJobId) {
          backgroundTaskQueue.trackHttpError(
            queueJobId,
            e?.response?.data?.error || e?.message || 'Error autenticando request'
          )
        }
        Session.clear()
        router.push({ name: 'Login' }).catch(() => {})
        return Promise.reject(e)
      }
    }

    const queueJobId = originalRequest?.metadata?.queueJobId
    if (queueJobId) {
      backgroundTaskQueue.trackHttpError(
        queueJobId,
        error?.response?.data?.error || error?.message || 'Error en request'
      )
    }

    return Promise.reject(error)
  }
)

export default instance
